import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MapPin, Plus, Edit2, Trash2, Save, ExternalLink, Star, X } from "lucide-react";
import { PageContainer } from "@/components/pro/PageContainer";
import { PageHeader } from "@/components/pro/PageHeader";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface Location {
  id: string;
  name: string;
  address_line1: string | null;
  address_line2: string | null;
  postal_code: string | null;
  city: string | null;
  country: string;
  notes: string | null;
  google_maps_url: string | null;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
}

const emptyLocation: Omit<Location, "id" | "created_at"> = {
  name: "",
  address_line1: "",
  address_line2: "",
  postal_code: "",
  city: "",
  country: "France",
  notes: "",
  google_maps_url: "",
  is_active: true,
  is_default: false,
};

export default function LocationsManagement() {
  const navigate = useNavigate();
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [formData, setFormData] = useState(emptyLocation);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadLocations();
  }, []);

  const loadLocations = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("locations")
        .select("*")
        .eq("user_id", user.id)
        .order("is_default", { ascending: false })
        .order("name");

      if (error) throw error;
      setLocations(data || []);
    } catch (error: any) {
      toast.error("Erreur lors du chargement des lieux");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingLocation(null);
    setFormData(emptyLocation);
    setDialogOpen(true);
  };

  const openEditDialog = (location: Location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      address_line1: location.address_line1 || "",
      address_line2: location.address_line2 || "",
      postal_code: location.postal_code || "",
      city: location.city || "",
      country: location.country,
      notes: location.notes || "",
      google_maps_url: location.google_maps_url || "",
      is_active: location.is_active,
      is_default: location.is_default,
    });
    setDialogOpen(true);
  };

  const generateGoogleMapsUrl = () => {
    const addressParts = [
      formData.address_line1,
      formData.address_line2,
      formData.postal_code,
      formData.city,
      formData.country,
    ].filter(Boolean);
    
    if (addressParts.length === 0) {
      toast.error("Veuillez renseigner une adresse");
      return;
    }

    const query = encodeURIComponent(addressParts.join(", "));
    const url = `https://www.google.com/maps/search/?api=1&query=${query}`;
    setFormData({ ...formData, google_maps_url: url });
    toast.success("Lien Google Maps généré");
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("Le nom du lieu est requis");
      return;
    }

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const locationData = {
        user_id: user.id,
        name: formData.name.trim(),
        address_line1: formData.address_line1?.trim() || null,
        address_line2: formData.address_line2?.trim() || null,
        postal_code: formData.postal_code?.trim() || null,
        city: formData.city?.trim() || null,
        country: formData.country || "France",
        notes: formData.notes?.trim() || null,
        google_maps_url: formData.google_maps_url?.trim() || null,
        is_active: formData.is_active,
        is_default: formData.is_default,
      };

      if (editingLocation) {
        const { error } = await supabase
          .from("locations")
          .update(locationData)
          .eq("id", editingLocation.id);

        if (error) throw error;
        toast.success("Lieu mis à jour");
      } else {
        const { error } = await supabase
          .from("locations")
          .insert(locationData);

        if (error) throw error;
        toast.success("Lieu créé");
      }

      setDialogOpen(false);
      loadLocations();
    } catch (error: any) {
      toast.error(error.message || "Erreur lors de la sauvegarde");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("locations")
        .delete()
        .eq("id", id);

      if (error) throw error;
      toast.success("Lieu supprimé");
      loadLocations();
    } catch (error: any) {
      toast.error("Erreur lors de la suppression");
      console.error(error);
    }
  };

  const toggleActive = async (location: Location) => {
    try {
      const { error } = await supabase
        .from("locations")
        .update({ is_active: !location.is_active })
        .eq("id", location.id);

      if (error) throw error;
      toast.success(location.is_active ? "Lieu désactivé" : "Lieu activé");
      loadLocations();
    } catch (error: any) {
      toast.error("Erreur lors de la mise à jour");
      console.error(error);
    }
  };

  const setAsDefault = async (location: Location) => {
    try {
      const { error } = await supabase
        .from("locations")
        .update({ is_default: true })
        .eq("id", location.id);

      if (error) throw error;
      toast.success("Lieu défini par défaut");
      loadLocations();
    } catch (error: any) {
      toast.error("Erreur lors de la mise à jour");
      console.error(error);
    }
  };

  const formatAddress = (location: Location) => {
    const parts = [
      location.address_line1,
      location.address_line2,
      `${location.postal_code || ""} ${location.city || ""}`.trim(),
    ].filter(Boolean);
    return parts.join(", ") || "Adresse non renseignée";
  };

  return (
    <PageContainer>
      <PageHeader
        title="Lieux de consultation"
        subtitle="Gérez vos différents lieux de consultation au cabinet"
        icon={<MapPin className="w-6 h-6 sm:w-7 sm:h-7" />}
        iconColor="primary"
        actions={
          <Button onClick={openCreateDialog} className="gap-2 bg-gradient-primary">
            <Plus className="w-4 h-4" />
            Ajouter un lieu
          </Button>
        }
      />

      <Card className="border-0 bg-card/80 backdrop-blur-sm shadow-card">
        <CardHeader>
          <CardTitle className="font-cinzel">Vos lieux de consultation</CardTitle>
          <CardDescription>
            Ces lieux seront proposés lors de la configuration de vos horaires d'ouverture pour les consultations au cabinet.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center animate-pulse">
                <MapPin className="w-6 h-6 text-primary" />
              </div>
              <p className="text-muted-foreground">Chargement...</p>
            </div>
          ) : locations.length === 0 ? (
            <div className="text-center py-12">
              <MapPin className="w-16 h-16 mx-auto mb-4 text-muted-foreground/40" />
              <h3 className="text-lg font-medium mb-2">Aucun lieu configuré</h3>
              <p className="text-muted-foreground mb-6">
                Ajoutez votre premier lieu de consultation pour pouvoir l'associer à vos créneaux cabinet.
              </p>
              <Button onClick={openCreateDialog} className="gap-2">
                <Plus className="w-4 h-4" />
                Ajouter un lieu
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {locations.map((location) => (
                <Card key={location.id} className={`p-4 ${!location.is_active ? "opacity-60" : ""}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-lg">{location.name}</h3>
                        {location.is_default && (
                          <Badge variant="default" className="gap-1">
                            <Star className="w-3 h-3" />
                            Par défaut
                          </Badge>
                        )}
                        {!location.is_active && (
                          <Badge variant="secondary">Inactif</Badge>
                        )}
                      </div>
                      <p className="text-muted-foreground text-sm">{formatAddress(location)}</p>
                      {location.notes && (
                        <p className="text-sm text-muted-foreground/80 mt-1 italic">{location.notes}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {location.google_maps_url && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(location.google_maps_url!, "_blank")}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      )}
                      {!location.is_default && location.is_active && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setAsDefault(location)}
                          title="Définir par défaut"
                        >
                          <Star className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleActive(location)}
                      >
                        <Switch checked={location.is_active} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(location)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Supprimer ce lieu ?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Les rendez-vous déjà confirmés conserveront l'adresse enregistrée (snapshot).
                              Cette action est irréversible.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annuler</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(location.id)}>
                              Supprimer
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit/Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingLocation ? "Modifier le lieu" : "Ajouter un lieu"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom du lieu *</Label>
              <Input
                id="name"
                placeholder="Ex: Cabinet principal, Maison Céleste..."
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address_line1">Adresse (ligne 1)</Label>
              <Input
                id="address_line1"
                placeholder="Numéro et rue"
                value={formData.address_line1 || ""}
                onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address_line2">Adresse (ligne 2)</Label>
              <Input
                id="address_line2"
                placeholder="Complément (bâtiment, étage...)"
                value={formData.address_line2 || ""}
                onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="postal_code">Code postal</Label>
                <Input
                  id="postal_code"
                  placeholder="31000"
                  value={formData.postal_code || ""}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">Ville</Label>
                <Input
                  id="city"
                  placeholder="Toulouse"
                  value={formData.city || ""}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Informations pratiques</Label>
              <Textarea
                id="notes"
                placeholder="Code d'accès, parking, transports..."
                value={formData.notes || ""}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="google_maps_url">Lien Google Maps</Label>
              <div className="flex gap-2">
                <Input
                  id="google_maps_url"
                  placeholder="https://www.google.com/maps/..."
                  value={formData.google_maps_url || ""}
                  onChange={(e) => setFormData({ ...formData, google_maps_url: e.target.value })}
                />
                <Button variant="outline" onClick={generateGoogleMapsUrl} type="button">
                  Générer
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Laissez vide pour générer automatiquement à partir de l'adresse
              </p>
            </div>

            <div className="flex items-center justify-between pt-2 border-t">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label>Lieu actif</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_default}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked })}
                />
                <Label>Lieu par défaut</Label>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={saving} className="gap-2">
              <Save className="w-4 h-4" />
              {saving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
